/**
 * Smith Opportunity Hub — Iter322.
 *
 * Cockpit that unifies every buying-signal collector the app currently
 * runs (ai_buying_signals, urgency alerts, foreclosure, sunbiz, SAM.gov,
 * job postings) into a single, filterable feed.
 *
 * Each card shows the 5 fields required by the Marcelo brief §8:
 *   • Which company / event owner
 *   • What happened
 *   • The evidence (source URL)
 *   • Why it might generate demand ("rationale")
 *   • What action to take
 *
 * Filter chips let the rep slice by taxonomy (explicit_need / external_
 * intent / inferred / own_engagement) and kind, matching the backend
 * taxonomy exactly. A "source health" strip surfaces coverage gaps so
 * empty buckets can be distinguished from "no data collected yet".
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Filter, ExternalLink, Radar, Zap,
  AlertOctagon, Building2, Search, HardHat,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import HelpButton from "../../components/tutorials/HelpButton";
import { useClaireContext } from "../../hooks/useClaireContext";
import FollowCompanyButton from "../../components/smith/FollowCompanyButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const TAX_COLORS = {
  explicit_need:   { bg: "bg-red-500/15",     bd: "border-red-400/40",     tx: "text-red-200",     label: "Explicit Need"    },
  external_intent: { bg: "bg-amber-500/15",   bd: "border-amber-400/40",   tx: "text-amber-200",   label: "External Intent"  },
  inferred:        { bg: "bg-blue-500/15",    bd: "border-blue-400/40",    tx: "text-blue-200",    label: "Inferred"         },
  own_engagement:  { bg: "bg-emerald-500/15", bd: "border-emerald-400/40", tx: "text-emerald-200", label: "Own Engagement"   },
};
const TAX_HINT = {
  explicit_need:   "Company published a demand or RFP",
  external_intent: "3rd-party signal (SAM.gov, LinkedIn Jobs, etc)",
  inferred:        "Event that may trigger demand — inspect before acting",
  own_engagement:  "Interaction observed on our channels (placeholder)",
};

const KIND_ICONS = {
  gov_bid: Building2, job_posting: HardHat, urgency: AlertOctagon,
  foreclosure: AlertOctagon, sunbiz_new: Building2, expansion: HardHat,
  contract_won: Building2, obra_awarded: HardHat, security_hiring: Radar,
  signal: Radar,
};

const friendlyError = (e, fb = "Failed") =>
  e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message || fb;

export default function SmithOpportunityHub() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [taxonomy, setTaxonomy] = useState("");
  const [kind, setKind] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: hub }, { data: st }] = await Promise.all([
        axios.get(`${API}/smith/opportunity-hub`, {
          params: { taxonomy: taxonomy || undefined,
                    kind: kind || undefined,
                    state: stateFilter || undefined,
                    limit: 80 }}),
        axios.get(`${API}/smith/opportunity-hub/status`),
      ]);
      setData(hub);
      setStatus(st);
    } catch (e) { toast.error(friendlyError(e, "Failed to load hub")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [taxonomy, kind, stateFilter]);

  const buckets = data?.buckets || {};
  const kinds = data?.kinds || [];
  const items = data?.items || [];

  const staleSources = useMemo(() => (status?.sources || []).filter(
    (s) => s.count === 0), [status]);

  // Iter326 · Publish Opportunity Hub context to Claire
  useClaireContext({
    page: "opportunity-hub",
    filters: {
      taxonomy: taxonomy || undefined,
      kind: kind || undefined,
      state: stateFilter || undefined,
    },
    visible_count: items.length,
    total_count: data?.total || items.length,
    project: "Smith Protective",
  });

  return (
    <div className="min-h-screen text-white" style={{ background: NAVY }}
         data-testid="smith-opportunity-hub-page">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="hub-header"
          title="Opportunity Hub"
          subtitle={`${data?.total || 0} signals · ${status?.sources?.length || 0} sources`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)", color: "white" }}>
              <Radar className="w-5 h-5" />
            </div>
          }
        >
          <HelpButton tutorialId="opportunity-hub" />
          <button type="button" onClick={load} disabled={loading}
            data-testid="hub-refresh"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-white/30 inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 max-w-[1500px] mx-auto pb-24 space-y-4">
          {/* Taxonomy chips */}
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-2" data-testid="hub-taxonomy-chips">
            {Object.entries(TAX_COLORS).map(([k, style]) => {
              const active = taxonomy === k;
              return (
                <button key={k} type="button"
                        onClick={() => setTaxonomy(active ? "" : k)}
                        data-testid={`hub-tax-${k}`}
                        className={`text-left p-3 rounded-xl border transition ${style.bg} ${style.bd} ${style.tx} ${active ? "ring-2 ring-white/40" : "hover:ring-1 ring-white/20"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10.5px] font-black uppercase tracking-widest">{style.label}</span>
                    <span className="ml-auto text-[16px] font-black tabular-nums">{buckets[k] ?? 0}</span>
                  </div>
                  <div className="text-[10px] opacity-80 mt-0.5 leading-snug">{TAX_HINT[k]}</div>
                </button>
              );
            })}
          </section>

          {/* Secondary filters */}
          <section className="rounded-xl border border-white/10 p-3 flex flex-wrap items-center gap-2"
                   style={{ background: "rgba(15,23,42,0.5)" }}>
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <select value={kind} onChange={(e) => setKind(e.target.value)}
                    data-testid="hub-kind-filter"
                    className="h-8 px-2 rounded bg-black/30 border border-white/15 text-[11.5px]">
              <option value="">All kinds ({kinds.length})</option>
              {kinds.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {k.kind.replace(/_/g, " ")} ({k.count})
                </option>
              ))}
            </select>
            <input type="text" value={stateFilter}
                   onChange={(e) => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
                   placeholder="State (2-letter)"
                   data-testid="hub-state-filter"
                   className="h-8 px-2 rounded bg-black/30 border border-white/15 text-[11.5px] w-32" />
            {(taxonomy || kind || stateFilter) && (
              <button type="button" onClick={() => { setTaxonomy(""); setKind(""); setStateFilter(""); }}
                      data-testid="hub-filters-clear"
                      className="text-[10.5px] font-bold text-slate-400 hover:text-white ml-auto">
                Clear filters
              </button>
            )}
          </section>

          {/* Source health strip — surfaces coverage gaps */}
          {staleSources.length > 0 && (
            <section className="rounded-lg border border-amber-400/25 bg-amber-500/5 p-2.5 text-[10.5px]"
                     data-testid="hub-coverage-gaps">
              <div className="flex items-center gap-2">
                <AlertOctagon className="w-3 h-3 text-amber-300" />
                <span className="font-bold text-amber-200 uppercase tracking-widest">Coverage gaps</span>
                <span className="text-amber-100/80">
                  {staleSources.map((s) => s.collection).join(" · ")} — 0 documents collected yet
                </span>
              </div>
            </section>
          )}

          {/* Signal feed */}
          <section data-testid="hub-feed" className="space-y-2">
            {loading && (
              <div className="py-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-[12px] text-slate-500 rounded-xl border border-white/10"
                   style={{ background: "rgba(15,23,42,0.5)" }}>
                No signals match those filters. Try clearing them or run <b>Rescan</b> on any followed company.
              </div>
            )}
            {items.map((row, i) => {
              const tax = TAX_COLORS[row.taxonomy] || TAX_COLORS.inferred;
              const Icon = KIND_ICONS[row.kind] || Radar;
              return (
                <div key={row.id || i}
                     data-testid={`hub-signal-${i}`}
                     className="rounded-xl border border-white/10 p-3 hover:border-white/25 transition"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`inline-flex items-center gap-1 text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${tax.bg} ${tax.tx} ${tax.bd} border`}>
                      {tax.label}
                    </span>
                    <Icon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[10.5px] font-black uppercase tracking-widest text-slate-300">
                      {(row.kind || "signal").replace(/_/g, " ")}
                    </span>
                    {row.confidence && (
                      <span className="text-[9.5px] text-slate-500">· {row.confidence} confidence</span>
                    )}
                    <span className="ml-auto text-[10px] text-slate-500 tabular-nums">
                      {row.detected_at ? new Date(row.detected_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-extrabold text-white truncate" title={row.company_name}>
                        {row.company_name}
                        {(row.city || row.state) && (
                          <span className="ml-2 text-[10.5px] text-slate-400 font-normal">
                            · {[row.city, row.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                      {row.headline && (
                        <div className="text-[12px] text-slate-200 mt-1 leading-relaxed">
                          {row.headline}
                        </div>
                      )}
                      {row.summary && row.summary !== row.headline && (
                        <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                          {row.summary}
                        </div>
                      )}
                      {/* Iter322 · Roles surface — brief §4: never confuse the
                          contract owner with the winner. When the source
                          knows, we show it; otherwise nothing rather than
                          fabricated defaults. */}
                      {row.roles && (
                        <div className="mt-1.5 text-[10.5px] flex flex-wrap gap-1.5"
                             data-testid={`hub-roles-${i}`}>
                          {Object.entries(row.roles).map(([r, v]) => v && (
                            <span key={r} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-200">
                              <b className="text-[9.5px] text-slate-500 uppercase tracking-widest">{r}</b>
                              <span className="truncate max-w-[220px]">{v}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 flex flex-col gap-1.5 items-end">
                      <FollowCompanyButton
                        company={{ company_name: row.company_name,
                                   city: row.city, state: row.state }}
                        testid={`hub-follow-${i}`}
                      />
                      {row.evidence_url && (
                        <a href={row.evidence_url} target="_blank" rel="noreferrer"
                           data-testid={`hub-evidence-${i}`}
                           className="inline-flex items-center gap-1 text-[10.5px] font-bold text-sky-300 hover:text-sky-200 underline decoration-dotted">
                          <ExternalLink className="w-3 h-3" /> Source
                        </a>
                      )}
                      <span className="text-[9.5px] text-slate-500">
                        {row.source_collection}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        </main>
      </div>
    </div>
  );
}
